import { Box } from '@mui/material';
import Decimal from 'decimal.js';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { BUSINESS_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { getEntityTypeFromSubtype } from '@/modules/entities/utils/getEntityTypeFromSubtype';
import { getTransferDiscountHelpText } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalTransfers/transferDiscount.utils';
import { formatPercent } from '@/utils/formatting/percent';
import { getNodes } from '@/utils/graphqlUtils';
import { validateNumberRange } from '@/utils/validators/validateNumberRange';

import { useTransferDestinationOptions } from '../../HypotheticalTransfersCard/hypotheticalTransfers/hooks/useTransferDestinationOptions';
import { TransferDirection } from '../../HypotheticalTransfersCard/hypotheticalTransfers/HypotheticalTransferForm';
import { useHypotheticalTransferModalContext } from '../HypotheticalTransferModal.context';
import { HypotheticalTransferFormModalShape } from '../HypotheticalTransferModal.types';

export function TransferDiscount() {
  const { householdId } = useHypotheticalTransferModalContext();
  const { data: destinationData } = useTransferDestinationOptions(
    householdId,
    TransferDirection.Outbound
  );
  const sourceNodes = getNodes(
    destinationData?.possibleHypotheticalTransferEntities
  );

  const { control, setValue } =
    useFormContext<HypotheticalTransferFormModalShape>();

  const [
    sourceId,
    isDiscountRateToggled,
    taxDiscountRate,
    sourceOwnedEntityId,
    transferAmount,
  ] = useWatch({
    control,
    name: [
      'sourceId',
      '_discountRateToggle',
      'taxDiscountRate',
      'sourceOwnedEntityId',
      'transferAmount',
    ],
  });

  const sourceNode = useMemo(() => {
    return sourceNodes.find((node) => node.id === sourceId);
  }, [sourceId, sourceNodes]);

  const switchLabel = useMemo(() => {
    return sourceNode?.subtype.effectiveDiscountPercent?.greaterThan(0)
      ? 'Override existing valuation discount'
      : 'Apply discount';
  }, [sourceNode?.subtype.effectiveDiscountPercent]);

  const switchCalloutText: string | undefined = useMemo(() => {
    let discountRate: Decimal | undefined =
      sourceNode?.subtype.effectiveDiscountPercent;

    if (sourceOwnedEntityId) {
      const sourceStake = sourceNode?.ownedOwnershipStakes?.find(
        (stake) => stake?.ownedEntity?.id === sourceOwnedEntityId
      );

      const entityType = sourceStake?.ownedEntity?.subtype.__typename;

      if (
        entityType &&
        BUSINESS_ENTITY_TYPES.includes(getEntityTypeFromSubtype(entityType))
      ) {
        discountRate =
          sourceStake?.ownedEntity?.subtype?.effectiveDiscountPercent;
      }
    }

    if (discountRate?.gt(0)) {
      return `Source entity has an effective ${formatPercent(discountRate, 2)}% valuation discount`;
    }

    return undefined;
  }, [
    sourceNode?.ownedOwnershipStakes,
    sourceNode?.subtype.effectiveDiscountPercent,
    sourceOwnedEntityId,
  ]);

  const calloutText = useMemo(
    () =>
      getTransferDiscountHelpText({
        transferAmount,
        discountRate: taxDiscountRate,
      }) ?? null,
    [taxDiscountRate, transferAmount]
  );

  useEffect(() => {
    return () => {
      setValue('taxDiscountRate', null);
    };
  }, [setValue]);

  let shouldShowDiscountRate = isDiscountRateToggled;

  return (
    <Card variant="filled" sx={{ p: 2 }}>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareSwitch<HypotheticalTransferFormModalShape>
            control={control}
            fieldName="_discountRateToggle"
            label={switchLabel}
            labelPosition="right"
            helpText={switchCalloutText}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {shouldShowDiscountRate && (
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <Box sx={{ pl: 7 }}>
              <FormAwarePercentInput<HypotheticalTransferFormModalShape>
                control={control}
                fieldName="taxDiscountRate"
                label="Discount rate"
                isDecimalJSInput
                disabled={!isDiscountRateToggled}
                required={true}
                validation={{
                  minMax: validateNumberRange({
                    min: 0,
                    max: 100,
                    fieldName: 'Discount rate',
                  }),
                }}
                helpText={calloutText ?? undefined}
              />
            </Box>
          </FormLayoutItem>
        </FormLayoutRow>
      )}
    </Card>
  );
}
